import React, { useRef } from 'react';
import FCategoryIcon, { resolveFCategory } from '../fCategoryIcons';

/**
 * Path marker only — goal copy lives in the side legend.
 * Dev mode: markers become draggable.
 */
export default function GoalJourneyMarker({
    goalNumber,
    title,
    category,
    accentColor = '#c9a227',
    isOolaOne = false,
    style,
    isActive = false,
    onHighlight,
    onClearHighlight,
    devMode = false,
    onDevDrag,
    coordLabel,
}) {
    const fName = resolveFCategory(category);
    const categoryLabel = fName || category;
    const ariaParts = [
        isOolaOne ? 'Oola One goal' : `Goal ${goalNumber}`,
        title || 'Goal',
        categoryLabel,
    ].filter(Boolean);
    const aria = ariaParts.join('. ');
    const dragRef = useRef({ active: false, moved: false });

    const startDrag = (e) => {
        if (!devMode || typeof onDevDrag !== 'function') return;
        e.preventDefault();
        e.stopPropagation();
        const pointerId = e.pointerId;
        dragRef.current = { active: true, moved: false };
        const target = e.currentTarget;
        try {
            target.setPointerCapture(pointerId);
        } catch {
            /* ignore */
        }

        const onMove = (ev) => {
            if (!dragRef.current.active) return;
            dragRef.current.moved = true;
            onDevDrag(ev.clientX, ev.clientY);
        };

        const onUp = () => {
            dragRef.current.active = false;
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
            window.removeEventListener('pointercancel', onUp);
            try {
                target.releasePointerCapture(pointerId);
            } catch {
                /* ignore */
            }
            target.classList.remove('is-dev-dragging-btn');
            const li = target.closest('.dv2-gjourney__marker');
            li?.classList.remove('is-dev-dragging');
        };

        target.classList.add('is-dev-dragging-btn');
        target.closest('.dv2-gjourney__marker')?.classList.add('is-dev-dragging');
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
        window.addEventListener('pointercancel', onUp);
    };

    const handleClick = (e) => {
        e.preventDefault();
        if (devMode) return;
        onHighlight?.();
    };

    return (
        <li
            className={`dv2-gjourney__marker${
                isOolaOne ? ' is-oola-one' : ''
            }${isActive ? ' is-active' : ''}${
                !devMode ? ' is-interactive' : ''
            }${devMode ? ' is-dev-drag' : ''}`}
            style={{
                ...style,
                ['--dv2-goal-accent']: accentColor,
            }}
        >
            <button
                type="button"
                className="dv2-gjourney__dot"
                aria-label={
                    devMode ? `${aria}. Drag to reposition.` : aria
                }
                aria-current={isActive ? 'true' : undefined}
                onClick={handleClick}
                onPointerDown={startDrag}
                onMouseEnter={() => {
                    if (!devMode) onHighlight?.();
                }}
                onFocus={() => {
                    if (!devMode) onHighlight?.();
                }}
                onMouseLeave={() => {
                    if (!devMode) onClearHighlight?.();
                }}
                onBlur={() => {
                    if (!devMode) onClearHighlight?.();
                }}
            >
                <FCategoryIcon category={category} size={isOolaOne ? 16 : 14} />
            </button>

            {devMode && coordLabel ? (
                <span className="dv2-gjourney__dev-coord">{coordLabel}</span>
            ) : null}
        </li>
    );
}
